import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { PortalImportEquivalence, PortalMemberImportService, SavePortalImportEquivalence } from '@core/api/services/portal-member-import.service';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { NotificationService } from '@shared/services/notification.service';
import { ActivityConfigurationDto } from '../../services/activity-management.service';
import { AssignmentDto } from '../../services/assignment-management.service';

export const SECTION_LABELS: Record<string, string> = {
  designations: 'Servicios', participations: 'Colaboraciones', countries: 'Países', organizationUnits: 'Unidades organizacionales',
};
export const TARGET_LABELS: Record<string, string> = {
  appointment: 'Encargo', assignment: 'Nombramiento', activity: 'Colaboración', country: 'País', organizationUnit: 'Unidad organizacional',
};
export const UNIT_LABELS: Record<string, string> = {
  portal: 'Unidad del Portal', membership: 'Comunidad de membresía', historical: 'Histórica si no existe', specific: 'Unidad específica',
};
export interface EquivalenceCatalogs {
  appointments: { id: number; name: string }[];
  assignments: AssignmentDto[];
  activities: ActivityConfigurationDto[];
  countries: { id: number; name: string }[];
}
interface EquivalenceEditorData {
  row?: PortalImportEquivalence;
  catalogs: EquivalenceCatalogs;
}

@Component({
  selector: 'app-portal-import-equivalence-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatInputModule, MatSelectModule, ModalShellComponent, UnitSelectorComponent],
  templateUrl: './portal-import-equivalence-editor.component.html',
  styleUrl: './portal-import-equivalence-editor.component.scss',
})
export class PortalImportEquivalenceEditorComponent {
  readonly data = inject<EquivalenceEditorData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<PortalImportEquivalenceEditorComponent>);
  private readonly api = inject(PortalMemberImportService);
  private readonly notifications = inject(NotificationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly sections = Object.entries(SECTION_LABELS).map(([value, label]) => ({ value, label }));
  readonly unitRules = Object.entries(UNIT_LABELS).map(([value, label]) => ({ value, label }));
  readonly modalConfig: ModalShellConfig = {
    title: this.data.row ? 'Editar equivalencia' : 'Nueva equivalencia',
    subtitle: 'Relacioná un valor del Portal con su destino en Comunidades.',
    size: 'lg', layout: 'form',
  };
  editing: SavePortalImportEquivalence = this.data.row ? { ...this.data.row } : {
    sourceSection: 'designations', sourceValue: '', targetKind: 'appointment', unitRule: 'portal', ignore: false,
  };
  saving = false;
  errorMessage = '';

  get isService(): boolean {
    return ['designations', 'participations'].includes(this.editing.sourceSection);
  }

  get options(): { id: number; name: string }[] {
    const kind = this.editing.targetKind;
    if (kind === 'country') return this.data.catalogs.countries;
    if (kind === 'appointment') return this.data.catalogs.appointments;
    const items = kind === 'assignment' ? this.data.catalogs.assignments : this.data.catalogs.activities;
    return items.filter(item => item.isActive || item.id === this.editing.targetId).map(item => ({ id: item.id!, name: item.name! }));
  }

  get roles() {
    return this.data.catalogs.activities.find(item => item.id === this.editing.targetId)?.roles
      .filter(role => role.isActive || role.id === this.editing.activityRoleId) ?? [];
  }

  changeSection(section: string): void {
    this.editing.sourceSection = section;
    this.editing.sourceLabel = undefined;
    this.changeKind(section === 'countries' ? 'country' : section === 'organizationUnits' ? 'organizationUnit' : 'appointment');
    this.editing.unitRule = 'portal';
    this.editing.targetReference = undefined;
  }

  changeKind(kind: string): void {
    this.editing.targetKind = kind;
    this.editing.targetId = undefined;
    this.editing.activityRoleId = undefined;
  }

  changeUnitRule(rule: string): void {
    this.editing.unitRule = rule;
    if (rule !== 'specific') this.editing.targetReference = undefined;
  }

  changeIgnore(ignore: boolean): void {
    this.editing.ignore = ignore;
    if (!ignore && this.editing.targetKind === 'ignore') {
      this.changeKind(this.editing.sourceSection === 'countries' ? 'country'
        : this.editing.sourceSection === 'organizationUnits' ? 'organizationUnit' : 'appointment');
    }
  }

  save(form: NgForm): void {
    if (this.saving) return;
    if (form.invalid || !this.editing.sourceValue.trim() || (!this.editing.ignore && (
      this.editing.sourceSection === 'organizationUnits' ? !this.editing.targetReference
        : !this.editing.targetId || this.isService && this.editing.unitRule === 'specific' && !this.editing.targetReference
    ))) {
      form.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.errorMessage = '';
    this.dialogRef.disableClose = true;
    const body: SavePortalImportEquivalence = {
      sourceSection: this.editing.sourceSection,
      sourceValue: this.editing.sourceValue.trim(),
      sourceLabel: this.editing.sourceLabel?.trim() || undefined,
      targetKind: this.editing.targetKind,
      ignore: this.editing.ignore,
      unitRule: this.editing.unitRule,
      targetId: this.editing.ignore ? undefined : this.editing.targetId,
      targetReference: this.editing.ignore ? undefined : this.editing.targetReference,
      activityRoleId: this.editing.ignore || this.editing.targetKind !== 'activity' ? undefined : this.editing.activityRoleId,
    };
    this.api.saveEquivalence(body, this.data.row?.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.notifications.success('Equivalencia guardada.');
        this.dialogRef.close(true);
      },
      error: error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.errorMessage = error?.error?.detail || error?.error?.message || 'No se pudo guardar. Revisá los datos de la equivalencia.';
        this.cdr.markForCheck();
      },
    });
  }
}
